import { useState } from 'react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/format'
import ConfirmDialog from './ConfirmDialog'
import { useFancyNotes } from './FancyNotePicker'

// The one click that moves a space's interviews: the brief plus one file per
// call become a fancy-notes meeting. Lives in the space header, beside the
// engagement's name, so it is there on both tabs. Server-side, so no
// transcript is ever downloaded here. The first send makes the meeting; later
// sends add the calls that arrived since to it — or start a new meeting when
// that one was deleted over there, which is why the button stays live after
// a send even with nothing new: the server says so, and sends nothing.
// Hidden, not disabled, when the server says the integration is off — a
// button that can never work is noise.
export default function SendToFancyNotes({ space, onChanged }) {
  const fancy = useFancyNotes()
  const [confirm, setConfirm] = useState(false)
  const [sending, setSending] = useState(false)
  const [status, setStatus] = useState(null) // { type: 'success' | 'error', text }

  if (fancy.status === 'off') return null

  // What a send would carry. First time: every call on the roster, evaluated
  // or not, plus the brief. After that: only the calls no send has carried,
  // counted by the server.
  const interviewCount = (space.staff ?? []).reduce((n, row) => n + (row.calls?.length ?? 0), 0)
  const hasBrief = Boolean((space.brief ?? '').trim())
  const sentBefore = Boolean(space.sent_to_fancy_notes_at)
  const unsentCount = space.unsent_call_count ?? 0
  const nothingNew = sentBefore && unsentCount === 0
  const canSend = sentBefore || interviewCount > 0 || hasBrief

  async function handleSend() {
    setSending(true)
    setStatus(null)
    try {
      const updated = await api(`/api/v1/spaces/${space.id}/send-to-fancy-notes`, {
        method: 'POST',
      })
      onChanged(updated)
      setStatus({ type: 'success', text: 'Sent to Fancy Notes.' })
    } catch (err) {
      setStatus({
        // 409 is the server saying "nothing new, and the meeting is still
        // there" — an answer, not a failure.
        type: err.status === 409 ? 'info' : 'error',
        text: err.detail || 'Could not send this space to Fancy Notes.',
      })
    } finally {
      setSending(false)
      setConfirm(false)
    }
  }

  return (
    <div className="send-notes">
      <button
        type="button"
        className="btn btn--dark btn--sm"
        onClick={() => setConfirm(true)}
        disabled={sending || !canSend}
        title={canSend ? undefined : 'Nothing to send yet — no brief and no interviews.'}
      >
        {sending
          ? 'Sending…'
          : nothingNew
            ? 'Send to Fancy Notes again'
            : sentBefore
              ? 'Send new interviews to Fancy Notes'
              : 'Send to Fancy Notes'}
      </button>
      {status ? (
        <p
          className={`send-notes__status${status.type === 'error' ? ' send-notes__status--error' : ''}`}
          role={status.type === 'error' ? 'alert' : 'status'}
        >
          {status.text}
          {status.type === 'success' && space.fancy_note_url && (
            <>
              {' '}
              <a
                className="md-link"
                href={space.fancy_note_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open the meeting
              </a>
            </>
          )}
        </p>
      ) : (
        space.sent_to_fancy_notes_at && (
          <p className="send-notes__status">
            Sent {formatDateTime(space.sent_to_fancy_notes_at)}
            {space.fancy_note_url && (
              <>
                {' · '}
                <a
                  className="md-link"
                  href={space.fancy_note_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open the meeting
                </a>
              </>
            )}
          </p>
        )
      )}

      {confirm && (
        <ConfirmDialog label="Send this space to Fancy Notes?" onClose={() => setConfirm(false)}>
          <h3 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
            Send this space to Fancy Notes?
          </h3>
          <p
            className="text-body-sm"
            style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-xs)' }}
          >
            {nothingNew
              ? `Nothing new has arrived since ${formatDateTime(space.sent_to_fancy_notes_at)}. Sending checks that the meeting still exists in Fancy Notes; if it was deleted there, a new one is created with everything, otherwise nothing is sent.`
              : sentBefore
              ? `${unsentCount === 1 ? 'One new interview' : `${unsentCount} new interviews`} — each with its evaluation and its full transcript — ${unsentCount === 1 ? 'is' : 'are'} added to the Fancy Notes meeting this space was sent to.`
              : `${hasBrief ? 'The brief and ' : ''}${interviewCount === 1 ? 'one interview' : `${interviewCount} interviews`} — each with its evaluation and its full transcript — become a new Fancy Notes meeting owned by hello@capston.ca and readable by everyone at Capston. The write-up is run from there.`}
          </p>
          {space.sent_to_fancy_notes_at && !nothingNew && (
            <p
              className="text-body-sm"
              style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-m)' }}
            >
              This space was sent {formatDateTime(space.sent_to_fancy_notes_at)}. If that meeting
              no longer exists there, a new one is created with everything.
              {space.fancy_note_url && (
                <>
                  {' '}
                  <a
                    className="md-link"
                    href={space.fancy_note_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open the meeting
                  </a>
                </>
              )}
            </p>
          )}
          <div className="flex justify-end flex-wrap" style={{ gap: 'var(--space-xs)' }}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setConfirm(false)}
              disabled={sending}
            >
              Cancel
            </button>
            <button type="button" className="btn btn--dark" onClick={handleSend} disabled={sending}>
              {sending ? 'Sending…' : 'Send to Fancy Notes'}
            </button>
          </div>
        </ConfirmDialog>
      )}
    </div>
  )
}
